import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { FileText, Upload, ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LetterheadSettings() {
  const [lh, setLh] = useState({ company_name: '', address: '', phone: '', email: '', website: '', tagline: '', logo_base64: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/settings/letterhead`).then(r => setLh(r.data || {})).catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { logo_base64, ...fields } = lh;
      await axios.put(`${API_URL}/api/settings/letterhead`, fields);
      toast.success('Letterhead saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1_500_000) { toast.error('Logo must be under 1.5MB'); return; }
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const base64 = ev.target.result;
      setLh(p => ({ ...p, logo_base64: base64 }));
      try {
        await axios.post(`${API_URL}/api/settings/letterhead/logo`, { logo_base64: base64 });
        toast.success('Logo uploaded');
      } catch { toast.error('Logo upload failed'); }
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-4" data-testid="letterhead-settings">
      <Card className="bg-white border-gray-border">
        <CardHeader>
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <FileText className="h-5 w-5 text-emerald-brand" />
            Letterhead & Branding
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-gray-500">This information appears on all your proposals, quotes, invoices, and documents.</p>

          <div className="flex items-center gap-4">
            <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center overflow-hidden bg-gray-50">
              {lh.logo_base64 ? (
                <img src={lh.logo_base64} alt="Logo" className="w-full h-full object-contain" />
              ) : (
                <ImageIcon className="h-8 w-8 text-gray-300" />
              )}
            </div>
            <div>
              <Label className="text-sm font-semibold">Company Logo</Label>
              <p className="text-xs text-gray-400 mb-2">PNG, JPG or SVG. Max 1.5MB.</p>
              <div>
                <input type="file" accept="image/*" className="hidden" id="logo-upload-input" onChange={handleLogoUpload} data-testid="logo-upload-input" />
                <Button variant="outline" size="sm" className="gap-1.5" onClick={() => document.getElementById('logo-upload-input')?.click()}>
                  <Upload className="h-3.5 w-3.5" /> Upload Logo
                </Button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>Company Name</Label><Input value={lh.company_name || ''} onChange={e => setLh(p => ({ ...p, company_name: e.target.value }))} placeholder="Your Company Name" data-testid="lh-company-name" /></div>
            <div><Label>Tagline</Label><Input value={lh.tagline || ''} onChange={e => setLh(p => ({ ...p, tagline: e.target.value }))} placeholder="Your company tagline" /></div>
          </div>
          <div><Label>Address</Label><Textarea value={lh.address || ''} onChange={e => setLh(p => ({ ...p, address: e.target.value }))} placeholder="Building 123, Road 456, City, Country" rows={2} /></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div><Label>Phone</Label><Input value={lh.phone || ''} onChange={e => setLh(p => ({ ...p, phone: e.target.value }))} placeholder="+973 1234 5678" /></div>
            <div><Label>Email</Label><Input value={lh.email || ''} onChange={e => setLh(p => ({ ...p, email: e.target.value }))} placeholder="hello@company.com" /></div>
            <div><Label>Website</Label><Input value={lh.website || ''} onChange={e => setLh(p => ({ ...p, website: e.target.value }))} placeholder="www.company.com" /></div>
          </div>

          <div className="mt-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
            <p className="text-xs text-gray-400 mb-2 font-semibold">PREVIEW</p>
            <div className="bg-white p-4 rounded-lg border shadow-sm">
              <div className="flex items-start gap-3">
                {lh.logo_base64 && <img src={lh.logo_base64} alt="Logo" className="w-12 h-12 object-contain" />}
                <div>
                  <p className="text-lg font-bold text-emerald-600">{lh.company_name || 'Company Name'}</p>
                  {lh.tagline && <p className="text-xs text-gray-400 italic">{lh.tagline}</p>}
                </div>
              </div>
              <div className="border-t border-emerald-500 mt-2 pt-2 text-[10px] text-gray-500 flex gap-3 flex-wrap">
                {lh.address && <span>{lh.address}</span>}
                {lh.phone && <span>{lh.phone}</span>}
                {lh.email && <span>{lh.email}</span>}
                {lh.website && <span>{lh.website}</span>}
              </div>
            </div>
          </div>

          <Button onClick={handleSave} disabled={saving} className="bg-emerald-brand hover:bg-emerald-hover" data-testid="save-letterhead-btn">
            {saving ? 'Saving...' : 'Save Letterhead'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
